import React from "react";
import styled from "styled-components";
import { DownloadSimple } from "@phosphor-icons/react";

import {
  CANVAS_HEIGHT,
  CANVAS_WIDTH,
  FRAME_OUTER_HEIGHT,
  FRAME_OUTER_WIDTH,
  FRAME_SLICE_LEFT,
  FRAME_SLICE_TOP,
} from "../../constants";
import {
  canCopyImageToClipboard,
  copyCompositeToClipboard,
  downloadComposite,
} from "../../composite";
import useEggLighting from "../../hooks/useEggLighting";
import useSubmissionRender from "../../hooks/useSubmissionRender";
import { eggImageUrl } from "../../paths";
import ActionButton from "../ActionButton";
import CompositeActionMenu from "../CompositeActionMenu";
import { dropShadowFilter, tintFilter } from "../DrawingArea/DrawingArea";
import FitContainer from "../FitContainer";
import ReferencePhoto from "../ReferencePhoto";

function GalleryCard({ item, href, meta, footer }) {
  const eggCanvasRef = React.useRef(null);
  const {
    drawingSrc,
    eggTransform,
    lights,
    warmth,
    redshift,
    marcTransform,
    strokes,
    palette,
  } = useSubmissionRender(item);

  useEggLighting({
    canvasRef: eggCanvasRef,
    eggSrc: eggImageUrl(item.eggId),
    eggTransform,
    frameW: CANVAS_WIDTH,
    frameH: CANVAS_HEIGHT,
    lights,
  });

  const [actionMenuPos, setActionMenuPos] = React.useState(null);
  const compositeReady = strokes != null && palette != null;
  const compositeOpts = React.useMemo(
    () => ({
      marcId: item.marcId,
      eggId: item.eggId,
      strokes,
      palette,
      eggTransform,
      marcTransform,
      lights,
      warmth,
      redshift,
    }),
    [
      item.marcId,
      item.eggId,
      strokes,
      palette,
      eggTransform,
      marcTransform,
      lights,
      warmth,
      redshift,
    ],
  );
  const handleContextMenu = (e) => {
    if (!compositeReady) return;
    e.preventDefault();
    setActionMenuPos({ x: e.clientX, y: e.clientY });
  };
  // Stop the link wrapper from navigating when the footer Download
  // button is clicked.
  const handleDownload = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!compositeReady) return;
    downloadComposite(compositeOpts);
  };

  const wrapperProps = href ? { as: "a", href } : {};

  return (
    <Card {...wrapperProps} onContextMenu={handleContextMenu}>
      <EggCol>
        <FrameStage>
          <FitContainer width={FRAME_OUTER_WIDTH} height={FRAME_OUTER_HEIGHT}>
            <FrameInterior>
              <EggCanvas
                ref={eggCanvasRef}
                width={CANVAS_WIDTH}
                height={CANVAS_HEIGHT}
                style={{
                  filter: `${tintFilter(warmth, redshift)} ${dropShadowFilter(
                    lights,
                  )}`,
                }}
              />
              {drawingSrc && (
                <DrawingImg src={drawingSrc} alt="" draggable={false} />
              )}
            </FrameInterior>
            <FrameOverlay src="/frame.png" alt="" draggable={false} />
          </FitContainer>
        </FrameStage>
      </EggCol>
      <SideCol>
        <MarcSlot>
          <ReferencePhoto
            marcId={item.marcId}
            marcTransform={marcTransform}
            withEasel={false}
          />
        </MarcSlot>
        <MetaSlot>{meta}</MetaSlot>
      </SideCol>
      <FooterRow>
        <FooterStart>{footer}</FooterStart>
        <ActionButton
          type="button"
          disabled={!compositeReady}
          onClick={handleDownload}
          title="Download a PNG of this Marc"
        >
          <DownloadSimple size={14} weight="bold" />
          <span>Download</span>
        </ActionButton>
      </FooterRow>
      <CompositeActionMenu
        open={actionMenuPos != null}
        onOpenChange={(next) => {
          if (!next) setActionMenuPos(null);
        }}
        anchorPos={actionMenuPos}
        onDownload={() => downloadComposite(compositeOpts)}
        onCopy={() => copyCompositeToClipboard(compositeOpts)}
        canCopy={canCopyImageToClipboard()}
      />
    </Card>
  );
}

const Card = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.35fr) minmax(0, 1fr);
  gap: clamp(0.5rem, 2vw, 1rem);
  padding: clamp(0.625rem, 2vw, 1rem);
  background: var(--color-orange-100);
  border: 1px solid var(--color-orange-300);
  border-radius: 10px;
  box-shadow: var(--surface-box-shadow);
  text-decoration: none;
  color: inherit;
  aspect-ratio: 1.1 / 1;
  /* align-items: center; */

  transition:
    background-color 120ms ease-out,
    border-color 120ms ease-out;

  &:hover {
    background: var(--color-orange-200);
    border-color: var(--color-orange-400);
  }
`;

const EggCol = styled.div`
  aspect-ratio: ${FRAME_OUTER_WIDTH} / ${FRAME_OUTER_HEIGHT};
  min-width: 0;
`;

const SideCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  min-width: 0;
`;

const MarcSlot = styled.div`
  width: 100%;
  aspect-ratio: ${CANVAS_WIDTH} / ${CANVAS_HEIGHT};
  container-type: size;
`;

const FrameStage = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  filter: drop-shadow(0 6px 10px rgba(0, 0, 0, 0.22));
`;

const FrameInterior = styled.div`
  position: absolute;
  top: ${FRAME_SLICE_TOP}px;
  left: ${FRAME_SLICE_LEFT}px;
  width: ${CANVAS_WIDTH}px;
  height: ${CANVAS_HEIGHT}px;
  background: var(--canvas-bg);
  overflow: hidden;
`;

const FrameOverlay = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: ${FRAME_OUTER_WIDTH}px;
  height: ${FRAME_OUTER_HEIGHT}px;
  pointer-events: none;
  user-select: none;
`;

const EggCanvas = styled.canvas`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
`;

const DrawingImg = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  user-select: none;
`;

const MetaSlot = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const FooterRow = styled.div`
  grid-column: 1 / -1;
  border-top: 1px solid var(--color-orange-300);
  padding-top: 0.625rem;
  margin-top: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
`;

// Holds whatever the parent passed as `footer`. Empty when no footer
// content is supplied (Mine tab) — the Download button still right-
// aligns via FooterRow's space-between.
const FooterStart = styled.div`
  min-width: 0;
`;

export default GalleryCard;
